import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowRightLeft } from "lucide-react";

const PersonSelect = ({ value, onChange, people, testid, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="mt-1"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
  </Select>
);

const PreviewList = ({ preview }) => (
  <div className="border border-slate-200 rounded-md max-h-60 overflow-y-auto" data-testid="handover-preview">
    {preview.count === 0 ? <div className="p-3 text-xs text-slate-500">No open tasks to move.</div> : preview.tasks.map((t) => (
      <div key={t.id} className="px-3 py-1.5 text-xs flex justify-between gap-2 border-b border-slate-100 last:border-0">
        <span className="truncate">{t.depth ? "↳ ".padStart(t.depth * 2 + 2, " ") : ""}{t.name}{t.client_name ? <span className="text-slate-400"> · {t.client_name}</span> : null}</span>
        <span className="mono text-slate-500 shrink-0">{t.due_date || "no due date"}</span>
      </div>
    ))}
  </div>
);

export const HandoverDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/users/assignable?team=1").then((r) => setPeople(r.data)).catch(() => {}); }, [open]);
  useEffect(() => {
    setPreview(null);
    if (!from || !to || from === to) return;
    api.post("/tasks/handover", { from_user_id: from, to_user_id: to, dry_run: true })
      .then((r) => setPreview(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [from, to]);
  const close = () => { setOpen(false); setFrom(""); setTo(""); setPreview(null); };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tasks/handover", { from_user_id: from, to_user_id: to, dry_run: false });
      toast.success(`${data.reassigned} task(s) handed over to ${data.to_name}`);
      close(); onDone?.();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button data-testid="handover-btn" variant="outline" className="gap-2" onClick={() => setOpen(true)}><ArrowRightLeft className="w-4 h-4" />Handover</Button>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg" data-testid="handover-dialog">
          <DialogHeader>
            <DialogTitle>Hand over open tasks</DialogTitle>
            <DialogDescription>Moves every open task and sub-task from one person to another. Completed tasks stay as they are.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-slate-500">From</Label>
              <PersonSelect value={from} onChange={setFrom} people={people} testid="handover-from" placeholder="Who is leaving" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-500">To</Label>
              <PersonSelect value={to} onChange={setTo} people={people.filter((p) => p.id !== from)} testid="handover-to" placeholder="Who takes over" /></div>
          </div>
          {preview && <div><div className="text-xs text-slate-600 mb-1" data-testid="handover-count">{preview.count} open task(s) will move</div><PreviewList preview={preview} /></div>}
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button data-testid="handover-confirm" disabled={!preview?.count || busy} onClick={confirm} className="bg-blue-700 hover:bg-blue-800 text-white">
              {busy ? "Moving…" : `Move ${preview?.count || 0} task(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
